import { Component, OnInit } from "@angular/core";
import { MessageService } from "primeng/api";
import { Subject } from "rxjs";
import { Company, Role } from "../interfaces/companies";
import { User, UserWithCompany } from "../interfaces/users";
import { UserManagementService } from "../services/user-management.service";

@Component({
  standalone: false,
  selector: "app-user-management",
  templateUrl: "./user-management.component.html",
  styleUrls: ["./user-management.component.scss"],
})
export class UserManagementComponent implements OnInit {
  userDialog: boolean;
  deletionDialog: boolean;
  inviteDialog: boolean;
  resetPasswordDialog: boolean;

  targetUser: User;
  editUser: UserWithCompany;
  company: Company;
  companyUpdated: boolean;
  newUser: boolean;

  newFoundUser: User;
  availableRoles: Role[];
  filterFormChanged: boolean;
  suggestions: string[] = [];
  suggestedCompanies: Company[] = [];
  onUpdate: Subject<Object> = new Subject<Object>();
  filteredCompanies: Company[] = [];

  buttonItems: any[];

  // This is a local copy from the userManagementService. It references the same object
  // When filtering is done, we change the reference to a local object instead
  companyUserMap: Record<string, User[]>;

  invalidEmail: boolean;
  invalidFirstName: boolean;
  invalidFamilyName: boolean;

  constructor(
    public userManagementService: UserManagementService,
    private messageService: MessageService
  ) {
    this.buttonItems = [
      {
        label: "Re-Invite",
        icon: "fas fa-rotate-right",
        command: () => {
          this.onReInviteUser();
        },
      },
      {
        label: "Reset Password",
        icon: "fas fa-key",
        command: () => {
          this.onResetPassword();
        },
      },
      {
        label: "Delete",
        icon: "fas fa-trash-alt",
        command: () => {
          this.onDeleteUser();
        },
      },
    ];
  }

  ngOnInit(): void {
    this.userManagementService.init();
    this.companyUserMap = this.userManagementService.companyUserMap;
  }

  setTargetUser(user: User) {
    this.targetUser = user;
  }

  openUpdateUserDialog(existingUser?: User) {
    this.userDialog = true;
    if (existingUser) {
      this.newUser = false;
      this.editUser = {
        email: existingUser.email,
        firstName: existingUser.firstName,
        familyName: existingUser.familyName,
        role: existingUser.role,
        companyCode: existingUser.companyCode,
        company:
          this.userManagementService.companyMap[existingUser.companyCode],
      };
      this.updateAvailableRoles(existingUser.companyCode);
    } else {
      this.newUser = true;
      this.availableRoles = [];
      this.editUser = {} as any;
    }
  }

  searchCompanies(event) {
    const regex = new RegExp(".*" + event.query + ".*", "i"); // Case Insensitive Regex substring query. Same as .contains() but insensitive
    this.suggestedCompanies = this.userManagementService.companies.filter(
      (company) => regex.test(company.name)
    );
  }

  search(event) {
    this.suggestions = [];
    const filteredCompanySet: Set<string> = new Set();
    this.filteredCompanies = [];
    this.companyUserMap = {}; // This doesn't change the data in the service. Just the reference locally
    if (event.query || typeof event === "string") {
      const query = event.query ? event.query : event;

      const emails = Object.keys(this.userManagementService.userMap);
      const filteredEmails = emails.filter((email) => email.includes(query));

      for (const email of filteredEmails) {
        const user = this.userManagementService.userMap[email];
        const companyCode = user.companyCode;

        if (!this.companyUserMap[companyCode]) {
          this.companyUserMap[companyCode] = [];
        }
        this.companyUserMap[companyCode].push(user);

        filteredCompanySet.add(companyCode);
      }

      for (const code of filteredCompanySet.values()) {
        this.filteredCompanies.push(
          this.userManagementService.companyMap[code]
        );
      }

      this.suggestions = filteredEmails;
    } else {
      this.filteredCompanies = [];
      this.companyUserMap = this.userManagementService.companyUserMap;
    }
  }

  onDeleteUser() {
    this.deletionDialog = true;
  }

  onReInviteUser() {
    this.inviteDialog = true;
  }

  onResetPassword() {
    this.resetPasswordDialog = true;
  }

  inviteUser() {
    this.userManagementService.reinviteUser(this.targetUser);
    this.hideDialog();
  }

  deleteUser() {
    this.userManagementService.deleteUser(this.targetUser);
    this.hideDialog();
  }

  resetPassword() {
    this.userManagementService.resetPassword(this.targetUser);
    this.hideDialog();
  }

  saveUser() {
    if (this.newUser) {
      this.userManagementService.addNewUser(this.editUser);
    } else {
      this.userManagementService.updateUser(this.editUser);
    }

    this.hideDialog();
  }

  onChangeCompany(selectedCompany: any) {
    this.editUser.companyCode = selectedCompany.value.code;
    this.updateAvailableRoles(selectedCompany.value.code);
  }

  updateAvailableRoles(company: string) {
    this.availableRoles = this.userManagementService.companyMap[company].roles;
  }

  handleError() {
    this.messageService.add({
      severity: "error",
      summary: "Unable to perform action",
      detail: `There was an issue modifying database \ \
      Please try again or contact your administrator for support.`,
      sticky: true,
    });
  }

  hideDialog() {
    this.userDialog = false;
    this.companyUpdated = false;
    this.deletionDialog = false;
    this.inviteDialog = false;
    this.resetPasswordDialog = false;
  }

  onEmailInput(event: any) {
    const inputValue = event.target.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    this.invalidEmail = !emailRegex.test(inputValue);
  }

  onFirstNameInput(event: any) {
    const inputValue = event.target.value;
    //Allowed characters: a-Z, spaces, and hyphens. Can not be empty. Must begin and end with a letter.
    this.invalidFirstName = !/^([A-Za-z]+[\w -]+)*[A-Za-z]+$/g.test(inputValue);
  }

  onFamilyNameInput(event: any) {
    const inputValue = event.target.value;
    //Allowed characters: a-Z, spaces, and hyphens. Can not be empty. Must begin and end with a letter.
    this.invalidFamilyName = !/^([A-Za-z]+[\w -]+)*[A-Za-z]+$/g.test(
      inputValue
    );
  }
}
